import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { selectUser } from 'entities/user/selectors';
import { ZendeskSetting } from 'entities/setting/model';
import { selectSetting } from 'entities/setting/selectors';
import Api from 'entities/api';

/* Every zE method here is duplicated because we want to support both Zendesk Widget versions */
declare const zE: (prompt1: string, prompt2: string, value?: unknown) => void;
declare const window: Window & { zE: typeof zE };

const AUTH_FAILED_TAG = 'jwt_auth_failed';

const loadScript = async (zendeskSettings: ZendeskSetting): Promise<void> => {
  if (!zendeskSettings) {
    return;
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = zendeskSettings.widgetId;
    script.src = `${zendeskSettings.widgetUrl}?key=${zendeskSettings.widgetSnippetKey}`;
    script.addEventListener('load', () => resolve());
    document.body.appendChild(script);
  });
};

const setTags = (tags: string[]) => {
  if (tags.length) {
    try {
      zE('messenger', 'addTags', tags);
    } catch (__) {}
    try {
      zE('webWidget', 'setVisitorInfo', { tags });
    } catch (__) {}
  }
};

const loginWithJwt = (tags: string[]) => {
  const callback = (setToken: (t?: string) => void) => {
    try {
      Api.get<{ token: string }>('api/zendesk/jwt-token').subscribe(
        (data: { token: string } | null) => {
          const token = data?.token || '';

          if (!token) {
            setToken();
            setTags([...tags, AUTH_FAILED_TAG]);
            return;
          }

          setToken(token);

          try {
            const { organization_id, organization } = jwtDecode<{
              organization_id?: string;
              organization?: string;
            }>(token);

            if (organization_id) {
              setTags([
                ...tags,
                `organization_id_${organization_id}`,
                `organization_${organization}`,
              ]);
            }
          } catch (__) {
            setTags([...tags, AUTH_FAILED_TAG]);
          }
        },
        () => {
          setToken();
          setTags([...tags, AUTH_FAILED_TAG]);
        }
      );
    } catch (__) {
      setToken();
      setTags([...tags, AUTH_FAILED_TAG]);
    }
  };

  try {
    zE('messenger', 'loginUser', callback);
  } catch (__) {}
  try {
    zE('webWidget', 'authenticate', { jwtFn: callback });
  } catch (__) {}
};

const handleReady = ({ tags }: { tags: string[] }) => {
  setTags(tags);
  loginWithJwt(tags);
};

const ZendeskWidget = ({
  zendeskSettings,
  isAuthenticated,
  tags,
  showChatWidget,
}: {
  zendeskSettings: ZendeskSetting | undefined;
  isAuthenticated: boolean;
  tags: string[];
  showChatWidget: boolean;
}) => {
  const [isLoaded, setIsLoaded] = useState(!!window.zE);

  // Load the Zendesk script
  useEffect(() => {
    if (!isLoaded && zendeskSettings) {
      loadScript(zendeskSettings).then(
        () => {
          setIsLoaded(true);
        },
        (e) => {
          // eslint-disable-next-line no-console
          console.error(`Failed to load zendesk: ${e}`);
        }
      );
    }

    return () => {
      if (typeof window.zE !== 'undefined') {
        try {
          zE('webWidget', 'hide');
        } catch (e) {
          zE('messenger', 'hide');
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zendeskSettings]);

  // Handle ready event to set the locale and tags and authenticate the user
  useEffect(() => {
    if (isLoaded && zendeskSettings) {
      try {
        zE('webWidget:on', 'ready', () => handleReady({ tags }));
      } catch (__) {}
      try {
        handleReady({ tags });
      } catch (__) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, zendeskSettings]);

  // React to auth transitions: re-authenticate on login; clear cache and logout on logout
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        zE('messenger', 'logoutUser');
      } catch (__) {}
      try {
        zE('webWidget', 'clearAuthentication');
      } catch (__) {}
      try {
        zE('webWidget', 'clear');
      } catch (__) {}
    }
  }, [isAuthenticated]);

  // Show the chat widget if the user has accepted cookies and the widget is enabled
  useEffect(() => {
    if (showChatWidget && typeof window.zE !== 'undefined') {
      try {
        zE('webWidget', 'show');
      } catch (__) {}
      try {
        zE('messenger', 'show');
      } catch (__) {}
    }
  }, [showChatWidget]);

  return null;
};

const SupportChatWidget = () => {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const user = useSelector(selectUser);
  const zendeskSettings = useSelector((state) => selectSetting(state, 'zendesk')) as
    | ZendeskSetting
    | undefined;

  const host = window.location.hostname || '';
  const envTag =
    host.includes('.testing.') || host.includes('.demo.') || host.includes('localhost')
      ? 'env_testing'
      : '';
  const tags: string[] = [envTag].filter(Boolean);

  if (user?.organization?.name) {
    tags.push(
      `organization_brand_${String(user.organization.name).replace(/\s+/g, '_').toLowerCase()}`
    );
  }

  if (user && isLarge && zendeskSettings) {
    return (
      <ZendeskWidget
        zendeskSettings={zendeskSettings}
        isAuthenticated={!!user}
        tags={tags}
        showChatWidget={Boolean(user && isLarge && zendeskSettings)}
      />
    );
  }
  return null;
};

export default SupportChatWidget;
